'use client'
import React from 'react'
import styled from 'styled-components'
import CheckIcon from '@mui/icons-material/Check'
import MonitorIcon from '@mui/icons-material/Monitor'
import SpeedIcon from '@mui/icons-material/Speed'
import { useConfig } from '@/contexts/ConfigContext'
import { useLanguage } from '@/contexts/LanguageContext'
import ModuleContainer from './ModuleContainer'
import ConfigSlider from '@components/Form/ConfigSlider'
import { Tooltip } from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

const RESOLUTIONS = {
    '4:3': [
        { width: 1440, height: 1080, tag: 'HD+' },
        { width: 1400, height: 1050 },
        { width: 1350, height: 1080 },
        { width: 1280, height: 1024, tag: '5:4' },
        { width: 1280, height: 960, tag: 'PROS', recommended: true },
        { width: 1152, height: 864 },
        { width: 1024, height: 768 },
        { width: 800, height: 600 },
        { width: 640, height: 480 },
    ],
    '16:9': [
        { width: 3840, height: 2160, tag: '4K UHD' },
        { width: 2560, height: 1440, tag: '2K QHD' },
        { width: 1920, height: 1080, tag: 'FULL HD', recommended: true },
        { width: 1600, height: 900 },
        { width: 1366, height: 768 },
        { width: 1360, height: 768 },
        { width: 1280, height: 720, tag: 'HD' },
        { width: 1152, height: 648 },
        { width: 1024, height: 576 },
    ],
    '16:10': [
        { width: 2560, height: 1600 },
        { width: 2304, height: 1440 },
        { width: 1920, height: 1200, recommended: true },
        { width: 1680, height: 1050 },
        { width: 1440, height: 900 },
        { width: 1280, height: 800 },
    ]
}

const Controls = styled.div`
    display: flex;
    flex-direction: column;
    gap: 30px;
    width: 100%;
    transition: opacity 0.2s ease;
`

const SettingsGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    background: rgba(0, 0, 0, 0.25);
    padding: 20px;
    width: 100%;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.05);
    box-shadow: inset 0 0 20px rgba(0, 0, 0, 0.2);
`

const ControlRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    color: #ccc;
    font-size: 0.9rem;
    min-height: 42px;
    padding: 10px 15px;
    background: rgba(255, 255, 255, 0.02);
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.03);
    transition: all 0.2s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.05);
        border-color: rgba(99, 102, 241, 0.2);
    }
`

const Checkbox = styled.div`
    width: 22px;
    height: 22px;
    border: 2px solid #666;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    background: ${({ checked }) => (checked ? '#6366f1' : 'transparent')};
    border-color: ${({ checked }) => (checked ? '#6366f1' : '#666')};
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: ${({ checked }) => (checked ? '0 0 10px rgba(99, 102, 241, 0.3)' : 'none')};
    opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    &:hover {
        border-color: #6366f1;
    }
`

const FullWidthSection = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 15px;
`

const OptionsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 6px;
    width: 100%;
`

const VideoGrid = styled.div`
    display: grid;
    grid-template-columns: 1.2fr 1fr;
    gap: 30px;
    width: 100%;

    @media (max-width: 800px) {
        grid-template-columns: 1fr;
    }
`

const SectionTitle = styled.h4`
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    color: #f163cdff;
    //color: #6366f1;
    font-weight: 600;
    font-family: var(--font-chakra);
    margin-bottom: 5px;
    display: flex;
    align-items: center;
    gap: 12px;

    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: linear-gradient(90deg, rgba(99, 102, 241, 0.4), transparent);
    }
`

const ResGridLabel = styled.div`
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.4);
    margin-bottom: 10px;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 400;
    font-family: var(--font-roboto);
`

const DropdownWrapper = styled.div`
    position: relative;
    width: 100%;
`

const DropdownTrigger = styled.div`
    background: rgba(0, 0, 0, 0.4);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 12px 18px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1);
    font-family: var(--font-roboto);
    font-size: 0.85rem;
    color: #f8fafc;
    font-weight: 400;
    min-height: 48px;
    width: 100%;
    opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    &:hover {
        border-color: #6366f1;
        background: rgba(99, 102, 241, 0.08);
        box-shadow: 0 0 20px rgba(99, 102, 241, 0.15);
    }

    svg {
        transition: transform 0.3s ease;
        transform: rotate(${({ $isOpen }) => ($isOpen ? '180deg' : '0deg')});
        font-size: 22px;
        color: #6366f1;
    }
`

const DropdownMenu = styled.div`
    position: absolute;
    top: calc(100% + 10px);
    left: 0;
    width: 100%;
    background: #0f172a;
    border: 1px solid rgba(99, 102, 241, 0.4);
    border-radius: 10px;
    z-index: 1000;
    max-height: 280px;
    overflow-y: auto;
    overflow-x: hidden;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(12px);
    display: flex;
    flex-direction: column;
    animation: fadeInSlide 0.2s ease-out;

    @keyframes fadeInSlide {
        from { opacity: 0; transform: translateY(-12px); }
        to { opacity: 1; transform: translateY(0); }
    }

    &::-webkit-scrollbar {
        width: 6px;
    }
    &::-webkit-scrollbar-thumb {
        background: #6366f1;
        border-radius: 10px;
    }
`

const RecBadge = styled.span`
    font-size: 0.6rem;
    font-weight: 900;
    background: #6366f1;
    color: #fff;
    padding: 2px 7px;
    border-radius: 4px;
    margin-left: 12px;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    box-shadow: 0 0 10px rgba(99, 102, 241, 0.3);
`

const DropdownOption = styled.div`
    padding: 14px 18px;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 0.82rem;
    color: ${({ $active }) => ($active ? '#fff' : 'rgba(248, 250, 252, 0.65)')};
    background: ${({ $active }) => ($active ? 'rgba(99, 102, 241, 0.18)' : 'transparent')};
    border-left: 4px solid ${({ $active }) => ($active ? '#6366f1' : 'transparent')};
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
    min-width: 0;
    white-space: nowrap;
    width: 100%;

    &:hover {
        background: rgba(255, 255, 255, 0.05);
        color: #fff;
        padding-left: 24px;
    }

    span.res-label {
        font-family: var(--font-roboto);
        font-weight: 600;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    span.res-tag {
        font-size: 0.65rem;
        opacity: 0.45;
        font-weight: 700;
        flex-shrink: 0;
        padding-left: 12px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    div.left-side {
        display: flex;
        align-items: center;
    }
`

const AspectButtonGroup = styled.div`
    display: flex;
    gap: 10px;
    width: 100%;
`

const AspectButton = styled.button`
    flex: 1;
    background: ${({ $active }) => ($active ? 'rgba(99, 102, 241, 0.18)' : 'rgba(0, 0, 0, 0.4)')};
    border: 1px solid ${({ $active }) => ($active ? '#6366f1' : 'rgba(255, 255, 255, 0.1)')};
    color: ${({ $active }) => ($active ? '#fff' : 'rgba(248, 250, 252, 0.5)')};
    padding: 12px;
    border-radius: 8px;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    font-family: var(--font-roboto);
    min-height: 44px;
    letter-spacing: 0.5px;
    opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    &:hover {
        border-color: #6366f1;
        background: rgba(99, 102, 241, 0.1);
        color: #fff;
    }

    &:active {
        transform: scale(0.96);
    }
`

const ControlGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 100%;
`

const CheckboxOption = ({ label, description, checked, onChange, disabled, style }) => (
    <ControlRow style={{ opacity: disabled ? 0.5 : 1, ...style }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 400, color: '#f8fafc', fontFamily: 'var(--font-roboto)' }}>
                    {label}
                </span>
                {description && (
                    <Tooltip title={description} placement="right" arrow>
                        <HelpOutlineIcon style={{ fontSize: 14, opacity: 0.3, cursor: 'help' }} />
                    </Tooltip>
                )}
            </div>
        </div>
        <Checkbox checked={checked} onClick={() => !disabled && onChange(!checked)} disabled={disabled}>
            {checked && <CheckIcon style={{ fontSize: 13, color: 'white' }} />}
        </Checkbox>
    </ControlRow>
)

export default function GraphicsModule({ id, initialExpanded }) {
    const { t } = useLanguage()
    const { activeModules, configData, updateConfig, updateConfigBatch, configName, configIsLocked } = useConfig()
    const [isDropdownOpen, setIsDropdownOpen] = React.useState(false)
    const dropdownRef = React.useRef(null)

    // Close dropdown on click outside
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const isGraphicsActive = activeModules.includes('graphics')
    const isVideoActive = activeModules.includes('video')
    const isActive = isGraphicsActive || isVideoActive

    const handleGraphicsChange = (key, value) => {
        updateConfig('graphics', key, value)
    }

    const handleVideoChange = (key, value) => {
        updateConfig('video', key, value)
    }

    const handleAspectChange = (newRatio) => {
        const available = RESOLUTIONS[newRatio] || []
        const updates = { aspect_ratio: newRatio }

        // If current resolution doesn't exist in new aspect ratio, pick the best one
        const currentRes = available.find(r => r.width === configData.video.resolution_width && r.height === configData.video.resolution_height)
        if (!currentRes && available.length > 0) {
            const best = available[available.length - 1]
            updates.resolution_width = best.width
            updates.resolution_height = best.height
        }

        updateConfigBatch('video', updates)
    }

    const currentAspect = configData.video.aspect_ratio || '16:9'
    const availableResolutions = RESOLUTIONS[currentAspect] || []

    return (
        <ModuleContainer
            id={id}
            moduleKey="graphics"
            title={t('GRAPHICS_TITLE')}
            icon={MonitorIcon}
            description={t('GRAPHICS_DESC')}
            initialExpanded={initialExpanded}
            isComingSoon={true}
        >
            <Controls isActive={isActive}>
                <FullWidthSection>
                    <SectionTitle>{t('VIDEO_TITLE')}</SectionTitle>
                    <SettingsGroup>
                        <VideoGrid>
                            {/* Column 1: Aspect & Resolution */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                    <ResGridLabel>{t('ASPECT_RATIO')}</ResGridLabel>
                                    <AspectButtonGroup>
                                        {['4:3', '16:9', '16:10'].map((ratio) => (
                                            <AspectButton
                                                key={ratio}
                                                $active={currentAspect === ratio}
                                                onClick={() => handleAspectChange(ratio)}
                                                disabled={configIsLocked}
                                            >
                                                {ratio}
                                            </AspectButton>
                                        ))}
                                    </AspectButtonGroup>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                    <ResGridLabel>{t('RESOLUTION')}</ResGridLabel>
                                    <DropdownWrapper ref={dropdownRef}>
                                        <DropdownTrigger
                                            $isOpen={isDropdownOpen}
                                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                            disabled={configIsLocked}
                                        >
                                            <span>
                                                {configData.video.resolution_width} {configName ? 'x' : ''} {configData.video.resolution_height}
                                            </span>
                                            <ExpandMoreIcon />
                                        </DropdownTrigger>

                                        {isDropdownOpen && (
                                            <DropdownMenu>
                                                {availableResolutions.map((res) => {
                                                    const isSelected = configData.video.resolution_width === res.width && configData.video.resolution_height === res.height
                                                    const resolutionText = `${res.width}x${res.height}`

                                                    return (
                                                        <DropdownOption
                                                            key={resolutionText}
                                                            $active={isSelected}
                                                            onClick={() => {
                                                                updateConfigBatch('video', {
                                                                    resolution_width: res.width,
                                                                    resolution_height: res.height
                                                                })
                                                                setIsDropdownOpen(false)
                                                            }}
                                                        >
                                                            <div className="left-side">
                                                                <span className="res-label">{resolutionText}</span>
                                                                {res.recommended && <RecBadge>{t('RECOMMENDED')}</RecBadge>}
                                                            </div>
                                                            {res.tag && <span className="res-tag">{res.tag}</span>}
                                                        </DropdownOption>
                                                    )
                                                })}
                                            </DropdownMenu>
                                        )}
                                    </DropdownWrapper>
                                </div>
                            </div>

                            {/* Column 2: Fullscreen & Refresh Rate */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                <ControlGroup>
                                    <ConfigSlider
                                        label={t('REFRESH_RATE')}
                                        description={t('REFRESH_RATE_DESC_HELP')}
                                        min={60}
                                        max={540}
                                        step={1}
                                        value={configData.video.refresh_rate}
                                        onChange={(val) => handleVideoChange('refresh_rate', val)}
                                        presets={[60, 90, 120, 144, 240]}
                                        showPresetsInline={true}
                                        disabled={configIsLocked}
                                    />
                                </ControlGroup>

                                <CheckboxOption
                                    label={t('FULLSCREEN')}
                                    checked={configData.video.fullscreen}
                                    onChange={(val) => handleVideoChange('fullscreen', val)}
                                    disabled={configIsLocked}
                                    style={{ marginTop: '16px' }}
                                />
                            </div>
                        </VideoGrid>
                    </SettingsGroup>
                </FullWidthSection>

                <FullWidthSection>
                    <SectionTitle>{t('GRAPHICS_TITLE')}</SectionTitle>
                    <ConfigSlider
                        label={t('FPS_MAX_LABEL')}
                        description={t('FPS_MAX_DESC')}
                        min={0}
                        max={360}
                        step={10}
                        value={configData.graphics.fps_max}
                        onChange={(val) => handleGraphicsChange('fps_max', val)}
                        presets={[
                            { label: t('UNLIMITED'), val: 0 },
                            60, 120, 144, 160, 240
                        ]}
                        showPresetsInline={true}
                        presetsPlacement="header"

                        disabled={configIsLocked}
                    />
                    <OptionsGrid>
                        <CheckboxOption
                            label={t('TRACERS')}
                            description={t('TRACERS_DESC')}
                            checked={configData.graphics.r_drawtracers_firstperson}
                            onChange={(val) => handleGraphicsChange('r_drawtracers_firstperson', val)}
                            disabled={configIsLocked}
                        />

                        <CheckboxOption
                            label={t('DYNAMIC_LIGHTING')}
                            description={t('DYNAMIC_LIGHTING_DESC')}
                            checked={configData.graphics.r_dynamic}
                            onChange={(val) => handleGraphicsChange('r_dynamic', val)}
                            disabled={configIsLocked}
                        />

                        <CheckboxOption
                            label={t('ANIMATED_AVATARS')}
                            description={t('ANIMATED_AVATARS_DESC')}
                            checked={configData.graphics.cl_allow_animated_avatars}
                            onChange={(val) => handleGraphicsChange('cl_allow_animated_avatars', val)}
                            disabled={configIsLocked}
                        />

                        <CheckboxOption
                            label={t('LOW_LATENCY')}
                            description={t('LOW_LATENCY_DESC')}
                            checked={configData.graphics.engine_low_latency_sleep_after_client_tick}
                            onChange={(val) => handleGraphicsChange('engine_low_latency_sleep_after_client_tick', val)}
                            disabled={configIsLocked}
                        />

                        <CheckboxOption
                            label={t('RAGDOLLS')}
                            description={t('RAGDOLLS_DESC')}
                            checked={configData.graphics.cl_ragdoll_workaround_threshold}
                            onChange={(val) => handleGraphicsChange('cl_ragdoll_workaround_threshold', val)}
                            disabled={configIsLocked}
                        />
                    </OptionsGrid>



                </FullWidthSection>
            </Controls>
        </ModuleContainer >
    )
}
